// /src/Components/Domain/TemplateSearchSelect.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  CheckCircle2,
  ChevronDown,
  ImageIcon,
  Search,
  X,
} from "lucide-react";

export default function TemplateSearchSelect({
  templates = [],
  value = "",
  disabled = false,
  loading = false,
  placeholder = "Select template",
  onChange,
}) {
  const wrapperRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");

  const selectedTemplate = useMemo(() => {
    return templates.find((item) => String(item?.id) === String(value)) || null;
  }, [templates, value]);

  const filteredTemplates = useMemo(() => {
    const q = search.trim().toLowerCase();

    if (!q) return templates;

    return templates.filter((item) => {
      return (
        String(item?.template_name || "").toLowerCase().includes(q) ||
        String(item?.template_code || "").toLowerCase().includes(q) ||
        String(item?.id || "").toLowerCase().includes(q)
      );
    });
  }, [templates, search]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!wrapperRef.current) return;

      if (!wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelect = (template) => {
    onChange?.(template);
    setOpen(false);
    setSearch("");
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange?.(null);
    setSearch("");
  };

  return (
    <div ref={wrapperRef} className="relative">
      <button
        type="button"
        disabled={disabled || loading}
        onClick={() => setOpen((prev) => !prev)}
        className="flex h-11 w-full items-center justify-between gap-3 rounded-xl border bg-white px-3 text-left text-sm outline-none transition hover:bg-gray-50 focus:border-emerald-400 disabled:cursor-not-allowed disabled:bg-gray-50 disabled:opacity-70"
      >
        {selectedTemplate ? (
          <div className="flex min-w-0 items-center gap-3">
            <TemplateThumb url={selectedTemplate.template_url} />

            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-gray-900">
                {selectedTemplate.template_name || "Untitled Template"}
              </p>

              <p className="truncate font-mono text-xs text-gray-400">
                ID: {selectedTemplate.id || "—"} ·{" "}
                {selectedTemplate.template_code || "—"}
              </p>
            </div>
          </div>
        ) : (
          <span className="text-gray-400">
            {loading ? "Loading templates..." : placeholder}
          </span>
        )}

        <div className="flex shrink-0 items-center gap-1">
          {selectedTemplate && !disabled && (
            <span
              role="button"
              tabIndex={0}
              onClick={handleClear}
              className="grid h-7 w-7 place-items-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
            >
              <X size={14} />
            </span>
          )}

          <ChevronDown
            size={16}
            className={`text-gray-400 transition ${open ? "rotate-180" : ""}`}
          />
        </div>
      </button>

      {open && !disabled && (
        <div className="absolute left-0 right-0 z-[80] mt-2 overflow-hidden rounded-2xl border bg-white shadow-xl">
          <div className="border-b bg-gray-50 p-3">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />

              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search template by name, code or ID..."
                className="h-10 w-full rounded-xl border bg-white pl-9 pr-3 text-sm outline-none focus:border-emerald-400"
                autoFocus
              />
            </div>
          </div>

          <div className="max-h-[360px] overflow-y-auto p-2">
            {filteredTemplates.length > 0 ? (
              filteredTemplates.map((template) => {
                const active = String(template?.id) === String(value);

                return (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => handleSelect(template)}
                    className={`mb-2 flex w-full items-center gap-3 rounded-xl border p-2 text-left transition last:mb-0 ${
                      active
                        ? "border-emerald-300 bg-emerald-50"
                        : "border-gray-100 bg-white hover:border-emerald-200 hover:bg-emerald-50/40"
                    }`}
                  >
                    <TemplateThumb url={template.template_url} large />

                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="truncate text-sm font-semibold text-gray-900">
                          {template.template_name || "Untitled Template"}
                        </p>

                        {active && (
                          <CheckCircle2
                            size={15}
                            className="shrink-0 text-emerald-600"
                          />
                        )}
                      </div>

                      <p className="mt-0.5 font-mono text-xs text-gray-500">
                        ID: {template.id || "—"}
                      </p>

                      <p className="mt-0.5 truncate font-mono text-xs text-gray-400">
                        Code: {template.template_code || "—"}
                      </p>
                    </div>
                  </button>
                );
              })
            ) : (
              <div className="px-3 py-8 text-center">
                <div className="mx-auto grid h-12 w-12 place-items-center rounded-xl bg-gray-50 text-gray-400">
                  <ImageIcon size={20} />
                </div>

                <p className="mt-3 text-sm font-semibold text-gray-800">
                  No templates found
                </p>

                <p className="mt-1 text-xs text-gray-400">
                  Try searching by template name, code or ID.
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TemplateThumb({ url, large = false }) {
  const [failed, setFailed] = useState(false);

  const sizeClass = large ? "h-16 w-24" : "h-9 w-12";

  if (!url || failed) {
    return (
      <div
        className={`grid shrink-0 place-items-center rounded-lg border bg-gray-50 text-gray-400 ${sizeClass}`}
      >
        <ImageIcon size={large ? 22 : 16} />
      </div>
    );
  }

  return (
    <img
      src={url}
      alt="Template preview"
      onError={() => setFailed(true)}
      className={`shrink-0 rounded-lg border bg-gray-50 object-cover ${sizeClass}`}
    />
  );
}